import React, { useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";

export const SHOWCASE_PRODUCT_VIEWS = ["grid", "list", "compact"];
export const showcaseProductViewKey = (scope) => `promoshowcase:products:view:${scope}`;

export const readShowcaseProductView = (scope) => {
  if (!scope) return "list";
  try {
    const saved = window.localStorage.getItem(showcaseProductViewKey(scope));
    return SHOWCASE_PRODUCT_VIEWS.includes(saved) ? saved : "grid";
  } catch (_error) {
    return "grid";
  }
};

// The scope is supplied only by Showcase. Other shared-cart consumers stay in List.
export const useShowcaseProductView = (scope) => {
  const [selection, setSelection] = useState(() => ({ scope, view: readShowcaseProductView(scope) }));
  const view = selection.scope === scope ? selection.view : readShowcaseProductView(scope);
  const selectView = (next) => {
    if (!scope || !SHOWCASE_PRODUCT_VIEWS.includes(next)) return;
    setSelection({ scope, view: next });
    try {
      window.localStorage.setItem(showcaseProductViewKey(scope), next);
    } catch (_error) {
      // A blocked or full browser store must not prevent switching views.
    }
  };
  return [view, selectView];
};

export const ShowcaseProductViewSelector = ({ view, onChange, orderingAvailable = true, compact = false }) => (
  <Box sx={{ mb: compact ? 0 : 2 }}>
    <ToggleButtonGroup exclusive size="small" value={view}
      onChange={(_event, next) => onChange(next)} aria-label="Showcase product view">
      {SHOWCASE_PRODUCT_VIEWS.map((value) => (
        <ToggleButton key={value} value={value} sx={{ textTransform: "capitalize" }}>{value}</ToggleButton>
      ))}
    </ToggleButtonGroup>
    {orderingAvailable && <Typography variant="caption" sx={{ display: "block", mt: .5, color: "text.secondary" }}>
      Drag to reorder is available in List when ordering is enabled.
    </Typography>}
  </Box>
);

const ProductImage = ({ row, compact }) => (
  <Box sx={{ width: compact ? 40 : "100%", height: compact ? 40 : { xs: 180, sm: 160, lg: 180 }, flexShrink: 0,
    display: "grid", placeItems: "center", bgcolor: "grey.50", overflow: "hidden",
    ...(!compact && { p: 1, boxSizing: "border-box", gridTemplateRows: "minmax(0, 1fr)", gridTemplateColumns: "minmax(0, 1fr)" }) }}>
    {row.image && <Box component="img" key={row.image} src={row.image} alt=""
      onError={(event) => { event.currentTarget.style.display = "none"; }}
      sx={{ width: "100%", height: "100%", objectFit: "contain", gridArea: "1 / 1", zIndex: 1, bgcolor: "grey.50",
        ...(!compact && { width: "auto", height: "auto", maxWidth: "100%", maxHeight: "100%", minWidth: 0, minHeight: 0, display: "block", objectPosition: "center" }) }} />}
    <Box sx={{ gridArea: "1 / 1", color: "text.disabled", textAlign: "center" }}>
      <ImageNotSupportedOutlinedIcon fontSize="small" />
      {!compact && <Typography variant="caption" display="block">No image</Typography>}
    </Box>
  </Box>
);

export const ShowcaseProductCard = ({ row, view, actions, summary, breakdown }) => {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const compact = view === "compact";
  const identity = <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
    <Typography variant={compact ? "subtitle2" : "body1"} title={row.name || "Unnamed product"}
      sx={compact ? {} : { fontWeight: 700, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4, minHeight: "2.8em" }}>
      {row.name || "Unnamed product"}
    </Typography>
    <Typography variant="caption" display="block" color="text.secondary">SKU: {row.sku || row.productIdDisplay || "—"}</Typography>
  </Box>;
  const pricingAction = <Button size="small" onClick={() => setBreakdownOpen(true)}>Show pricing breakdown</Button>;
  const dialog = <Dialog open={breakdownOpen} onClose={() => setBreakdownOpen(false)} fullWidth maxWidth="lg"
    aria-labelledby={`showcase-pricing-${row.layoutId || row.itemId}`}>
    <DialogTitle id={`showcase-pricing-${row.layoutId || row.itemId}`}>Pricing breakdown — {row.name || "Unnamed product"}</DialogTitle>
    <DialogContent dividers sx={{ overflowX: "auto" }}>
      {breakdownOpen && <Box sx={{ minWidth: 520 }}>{breakdown}</Box>}
    </DialogContent>
    <DialogActions><Button onClick={() => setBreakdownOpen(false)}>Close</Button></DialogActions>
  </Dialog>;
  if (compact) return <>
    <TableRow>
      <TableCell><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><ProductImage row={row} compact />{identity}</Box></TableCell>
      <TableCell sx={{ overflowWrap: "anywhere" }}>{row.supplier || "—"}</TableCell>
      <TableCell>{summary}</TableCell>
      <TableCell>{actions}{pricingAction}</TableCell>
    </TableRow>
    {dialog}
  </>;
  return <Paper variant="outlined" sx={{ minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
    <ProductImage row={row} />
    <Box sx={{ p: 2, flex: 1 }}>{identity}<Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>{row.supplier || "—"}</Typography></Box>
    {actions}
    {pricingAction}
    {dialog}
  </Paper>;
};

export const ShowcaseProductLayout = ({ rows, view, renderProduct, renderSection }) => {
  const renderRow = (row, index) => row.type === "section"
    ? view === "compact"
      ? <TableRow key={row.layoutId}><TableCell colSpan={4}>{renderSection(row)}</TableCell></TableRow>
      : <Box key={row.layoutId} sx={{ gridColumn: "1 / -1" }}>{renderSection(row)}</Box>
    : <React.Fragment key={row.layoutId || row.itemId || index}>{renderProduct(row, index)}</React.Fragment>;
  if (view === "compact") return <TableContainer tabIndex={0} role="region" aria-label="Compact Showcase products" sx={{ overflowX: "auto" }}>
    <Table size="small" sx={{ minWidth: 760 }}>
      <TableHead><TableRow>
        <TableCell>Product / SKU</TableCell><TableCell>Supplier</TableCell>
        <TableCell>Quantity / Margin / Subtotal</TableCell><TableCell>Actions</TableCell>
      </TableRow></TableHead>
      <TableBody>{rows.map(renderRow)}</TableBody>
    </Table>
  </TableContainer>;
  return <Box sx={{ containerType: "inline-size", containerName: "showcase-products", minWidth: 0 }}>
    <Box data-showcase-grid sx={{ display: "grid", gap: 2, gridTemplateColumns: "minmax(0, 1fr)",
      "@container showcase-products (min-width: 560px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      "@container showcase-products (min-width: 840px)": { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
      "@container showcase-products (min-width: 1120px)": { gridTemplateColumns: "repeat(4, minmax(0, 1fr))" },
    }}>{rows.map(renderRow)}</Box>
  </Box>;
};
